import { c as _c } from "react/compiler-runtime";
import { type SheetTypes } from '@svenskaspelab/poolbetting/schema/input';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CLOSE_SHEET_EVENT, OPEN_SHEET_EVENT, type OpenSheetEventDetail } from '@/lib/index.browser';

/**
 * Hook that manages optimistic sheet state and event listeners.
 * Syncs with query params and handles open/close events.
 *
 * @param sheetFromQuery Sheet value from query params
 * @param validSheets Valid sheet types for this route
 * @returns Active sheet and whether it's locked during content load
 */
export function useSheetState(sheetFromQuery, validSheets) {
  const $ = _c(10);
  const filteredSheetFromQuery = sheetFromQuery !== undefined && validSheets.includes(sheetFromQuery) ? sheetFromQuery : undefined;
  const [optimisticSheet, setOptimisticSheet] = useState(undefined);
  const previousSheetFromQueryRef = useRef(filteredSheetFromQuery);
  let t0;
  let t1;
  if ($[0] !== filteredSheetFromQuery || $[1] !== optimisticSheet) {
    t0 = () => {
      const previousSheetFromQuery = previousSheetFromQueryRef.current;
      previousSheetFromQueryRef.current = filteredSheetFromQuery;
      const isQueryTransitionedToClosed = previousSheetFromQuery !== undefined && filteredSheetFromQuery === undefined;
      if (optimisticSheet !== undefined && isQueryTransitionedToClosed) {
        const resetId = window.setTimeout(() => {
          setOptimisticSheet(undefined);
        }, 0);
        return () => {
          window.clearTimeout(resetId);
        };
      }
    };
    t1 = [filteredSheetFromQuery, optimisticSheet];
    $[0] = filteredSheetFromQuery;
    $[1] = optimisticSheet;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[4] !== validSheets) {
    t2 = () => {
      const handleOpenSheet = event => {
        const customEvent = event as CustomEvent<OpenSheetEventDetail>;
        if (!validSheets.includes(customEvent.detail.sheet)) {
          return;
        }
        setOptimisticSheet(customEvent.detail.sheet);
      };
      const handleCloseSheet = () => {
        setOptimisticSheet(undefined);
      };
      window.addEventListener(OPEN_SHEET_EVENT, handleOpenSheet);
      window.addEventListener(CLOSE_SHEET_EVENT, handleCloseSheet);
      return () => {
        window.removeEventListener(OPEN_SHEET_EVENT, handleOpenSheet);
        window.removeEventListener(CLOSE_SHEET_EVENT, handleCloseSheet);
      };
    };
    t3 = [validSheets];
    $[4] = validSheets;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  const isTransitioningBetweenSheets = optimisticSheet !== undefined && filteredSheetFromQuery !== undefined && optimisticSheet !== filteredSheetFromQuery;
  const activeSheet = isTransitioningBetweenSheets ? optimisticSheet : filteredSheetFromQuery ?? optimisticSheet;
  const isSheetLocked = optimisticSheet !== undefined && filteredSheetFromQuery !== optimisticSheet;
  let t4;
  if ($[7] !== activeSheet || $[8] !== isSheetLocked) {
    t4 = {
      activeSheet,
      isSheetLocked
    };
    $[7] = activeSheet;
    $[8] = isSheetLocked;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}